import { z } from "zod";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  sirenOrSiretSchema,
  vatSchema,
  validateVatWithSiren,
} from "../utils/auth_utils.schema";

export const GenderSchema = z.enum(["male", "female", "other"]);

export const ModifyProfileSchema = z
  .object({
    picture: z.string().nullable(),
    firstName: z
      .string()
      .min(2, "Veuillez entrer un prénom valide")
      .max(64, "Veuillez entrer un prénom valide")
      .optional(),
    lastName: z
      .string()
      .min(2, "Veuillez entrer un nom valide")
      .max(64, "Veuillez entrer un nom valide")
      .optional(),
    gender: GenderSchema.nullable(),
    dateOfBirth: z.date().nullable(),
    email: z.string().email("Veuillez entrer un email valide").optional(),
    phoneNumber: z
      .string()
      .nullable()
      .refine(
        (phone) => {
          if (!phone) return true;
          return isValidPhoneNumber(phone, "FR");
        },
        {
          message: "Téléphone invalide",
        }
      ),
    address: z.string().nullable(),
    city: z.string().nullable(),
    zipCode: z.string().nullable(),
    interest: z.string().nullable(),
    businessName: z.string().nullable(),
    isProfessional: z.boolean().optional(),
    siret: sirenOrSiretSchema.nullable(),
    vat: vatSchema.nullable(),
    country: z.string().optional().nullable(),
    sharedInformations: z
      .object({
        firstName: z.boolean().optional(),
        lastName: z.boolean().optional(),
        email: z.boolean().optional(),
      })
      .optional(),
  })
  .partial()
  .superRefine((form, ctx) => {
    const {
      sharedInformations,
      firstName,
      lastName,
      email,
      siret,
      country,
      vat,
    } = form;

    if (sharedInformations === undefined) {
      return;
    }

    const {
      firstName: shareFirstName,
      lastName: shareLastName,
      email: shareEmail,
    } = sharedInformations;

    checkFieldRequired(shareFirstName, firstName, "firstName", ctx);
    checkFieldRequired(shareLastName, lastName, "lastName", ctx);
    checkFieldRequired(shareEmail, email, "email", ctx);

    if (siret !== undefined && !country) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Vous devez renseigner le pays de votre activité",
        path: ["country"],
      });
    }

    if (siret !== undefined && vat === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Vous devez renseigner le numéro de TVA",
        path: ["vat"],
      });
    }

    validateVatWithSiren(ctx, vat, siret);
  });

const checkFieldRequired = <T>(
  isShared = false,
  value: T,
  path: string,
  ctx: z.RefinementCtx
) => {
  if (isShared && (!value || value === "")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: "Vous devez renseigner ce champ",
    });
  }
};

export const defaultModifyProfileValues = {
  picture: "",
  firstName: "",
  lastName: "",
  gender: null,
  dateOfBirth: new Date(),
  email: "",
  phoneNumber: "",
  address: "",
  city: "",
  zipCode: "",
  interest: "",
  businessName: "",
  sharedInformations: {
    email: true,
    firstName: true,
    lastName: true,
  },
  siret: undefined,
  vat: undefined,
  country: null,
};

export type ModifyProfileForm = z.infer<typeof ModifyProfileSchema>;
